import React from 'react';
import Block from '../../design-system/chat-blocks/Block';
import Pill from '../../design-system/primitives/Pill';
import { fmtInt } from './format';

// Same thresholds as the account page's token bar.
const barColor = (pct) => (pct >= 90 ? 'var(--danger)' : pct >= 70 ? 'var(--warn-500)' : 'var(--foreground)');

const QuotaBar = ({ label, used, max, unlimited, own, noun }) => {
  const capped = !unlimited && max != null;
  const pct = capped && max ? Math.min(100, Math.round((used / max) * 100)) : null;
  return (
    <div className="qa-quota">
      <div className="qa-quota__head">
        <span className="qa-quota__label">{label}</span>
        <span className="qa-quota__count">
          {fmtInt(used)}{capped ? ` / ${fmtInt(max)}` : ' · unlimited'}
        </span>
      </div>
      {capped && (
        <div className="qa-quota__track">
          <div className="qa-quota__fill" style={{ width: `${pct ?? 0}%`, background: barColor(pct ?? 0) }} />
        </div>
      )}
      {own != null && (
        <div className="qa-quota__own">{fmtInt(own)} by this {noun}</div>
      )}
      {pct != null && <div className="qa-quota__pct" style={{ color: pct >= 90 ? 'var(--danger)' : undefined }}>{pct}% of period allowance</div>}
    </div>
  );
};

// What the bars count, in words: whose usage the governing limit adds up.
const governingLabel = (g, isUser) => {
  if (g.scope === 'staff') return 'Staff account · unlimited';
  if (g.scope === 'none') return 'No limits row · unlimited by default';
  if (g.is_entity) return isUser ? 'Personal limits' : 'Organization-wide limits';
  return g.scope === 'customer' ? `Counts ${g.name} only` : `Counts all of ${g.name}`;
};

const UsageLimitsBlock = ({ usage, limits, counters, entity }) => {
  const g = usage.governing;
  const isUser = entity.type === 'user';
  const noun = isUser ? 'user' : 'organization';
  const unlimited = limits.is_unlimited;
  const fits = counters.api_fits;
  const preds = counters.api_predictions;
  const cap = (n, max) => (max != null ? `${n}/${max}` : `${n}`);
  const guardsNote = isUser
    ? `${cap(fits.used_last_hour, limits.max_api_fits_per_hour)} fits · ${cap(preds.used_last_hour, limits.max_api_predictions_per_hour)} predictions and forecasts in the last hour`
    : `busiest member in the last hour: ${cap(fits.peak_member_last_hour, limits.max_api_fits_per_hour)} fits · ${cap(preds.peak_member_last_hour, limits.max_api_predictions_per_hour)} predictions and forecasts (enforced per user; ${fits.used_last_hour} and ${preds.used_last_hour} across all members)`;
  const own = usage.entity_period;
  return (
    <Block
      icon="gauge"
      title="Usage vs. limits"
      badge={<Pill tone={g.is_entity ? 'neutral' : 'outline'} style={g.is_entity ? undefined : { color: 'var(--warn-500)', borderColor: 'var(--warn-500)' }}>{governingLabel(g, isUser)}</Pill>}
      footer={
        <span>
          Hourly API guards: {guardsNote}
          {limits.access_day_limit != null && ` · access window ${limits.access_day_limit} days`}
        </span>
      }
    >
      {!g.is_entity && own && (
        <p className="qa-note qa-note--warn">
          The bars add up {g.scope === 'customer' ? `${g.name}'s usage` : `all of ${g.name}`}, because that is what enforcement compares this {noun} against.
          This {noun}'s own usage in the same period is shown under each bar.
        </p>
      )}
      <div className="qa-quota-grid">
        <QuotaBar label="Analyses" used={g.analyses.used} max={g.analyses.max} unlimited={unlimited} own={own?.analyses} noun={noun} />
        <QuotaBar label="Chat messages" used={g.chat_messages.used} max={g.chat_messages.max} unlimited={unlimited} own={own?.chat_messages} noun={noun} />
        <QuotaBar label="Output tokens" used={g.output_tokens.used} max={g.output_tokens.max} unlimited={unlimited} own={own?.output_tokens} noun={noun} />
      </div>
      <p className="qa-note">
        All time, this {noun}: {fmtInt(usage.all_time.analyses)} analyses · {fmtInt(usage.all_time.chat_messages)} chat messages · {fmtInt(usage.all_time.output_tokens)} billed output tokens.
      </p>
    </Block>
  );
};

export default UsageLimitsBlock;
